/**
 * Caixa de escrever (F4): Enter envia, Shift+Enter quebra linha, @ sugere pessoas,
 * anexos por botão, colar (imagem) ou arrastar. Anexos precisam de internet: o
 * arquivo sobe para o servidor antes de a mensagem ir para a fila.
 */
import { createMemo, createSignal, For, Show } from 'solid-js'
import { Button } from '@ui/components/Button'
import type { ChatAnexo, ChatUsuario } from '@shared/types'
import { anexos as anexosApi, estadoSync } from '../../nucleo/cliente'
import { mensagemErro } from '../../lib/erro'
import { tamanho } from './util'

type Pendente = { id: number; arquivo: File; enviado?: Omit<ChatAnexo, 'id'>; erro?: string }
const LIMITE = 25 * 1024 * 1024
let seq = 0

export function Compositor(props: {
  placeholder: string
  usuarios: ChatUsuario[]
  desabilitado?: string | null
  aoEnviar: (texto: string, anexos: Array<Omit<ChatAnexo, 'id'>>) => Promise<void>
}) {
  let campo: HTMLTextAreaElement | undefined
  let seletor: HTMLInputElement | undefined
  const [texto, setTexto] = createSignal('')
  const [pendentes, setPendentes] = createSignal<Pendente[]>([])
  const [erro, setErro] = createSignal<string | null>(null)
  const [enviando, setEnviando] = createSignal(false)
  const [sobre, setSobre] = createSignal(false)
  const [mencao, setMencao] = createSignal<{ termo: string; inicio: number } | null>(null)
  const [realce, setRealce] = createSignal(0)
  const online = () => estadoSync()?.online !== false

  const sugestoes = createMemo(() => {
    const m = mencao()
    if (!m) return []
    const t = m.termo.toLowerCase()
    return props.usuarios.filter((u) => u.login.toLowerCase().startsWith(t) || u.nome.toLowerCase().split(' ').some((p) => p.startsWith(t))).slice(0, 6)
  })

  const ajustarAltura = () => {
    if (!campo) return
    campo.style.height = 'auto'
    campo.style.height = `${Math.min(campo.scrollHeight, 220)}px`
  }
  const detectarMencao = () => {
    if (!campo) return
    const antes = campo.value.slice(0, campo.selectionStart)
    const m = /(^|\s)@([\p{L}\p{N}._-]*)$/u.exec(antes)
    setMencao(m ? { termo: m[2], inicio: antes.length - m[2].length - 1 } : null)
    setRealce(0)
  }
  const escolher = (u: ChatUsuario) => {
    const m = mencao()
    if (!m || !campo) return
    const v = campo.value
    const fim = campo.selectionStart
    const novo = `${v.slice(0, m.inicio)}@${u.login} ${v.slice(fim)}`
    setTexto(novo)
    campo.value = novo
    const pos = m.inicio + u.login.length + 2
    campo.setSelectionRange(pos, pos)
    setMencao(null)
    campo.focus()
  }

  const adicionar = (arquivos: FileList | File[] | null) => {
    if (!arquivos) return
    setErro(null)
    const novos: Pendente[] = []
    for (const f of Array.from(arquivos)) {
      if (f.size > LIMITE) {
        setErro(`"${f.name}" passa de 25 MB.`)
        continue
      }
      novos.push({ id: ++seq, arquivo: f })
    }
    if (pendentes().length + novos.length > 10) return setErro('No máximo 10 anexos por mensagem.')
    setPendentes([...pendentes(), ...novos])
  }

  const enviar = async () => {
    const t = texto().trim()
    if ((!t && pendentes().length === 0) || enviando()) return
    setErro(null)
    setEnviando(true)
    try {
      // 1) sobe os arquivos (precisa de internet)
      const lista = [...pendentes()]
      for (const p of lista) {
        if (p.enviado) continue
        try {
          p.enviado = await anexosApi.enviar(p.arquivo)
        } catch (e) {
          p.erro = mensagemErro(e)
          setPendentes([...lista])
          throw e
        }
      }
      // 2) a mensagem (vai pela fila: funciona offline se não tiver anexo)
      await props.aoEnviar(t, lista.map((p) => p.enviado!))
      setTexto('')
      if (campo) campo.value = ''
      setPendentes([])
      ajustarAltura()
    } catch (e) {
      setErro(mensagemErro(e))
    } finally {
      setEnviando(false)
      campo?.focus()
    }
  }

  return (
    <div
      class="relative px-4 pb-4"
      onDragOver={(e) => (e.preventDefault(), setSobre(true))}
      onDragLeave={() => setSobre(false)}
      onDrop={(e) => (e.preventDefault(), setSobre(false), adicionar(e.dataTransfer?.files ?? null))}
    >
      <Show when={sugestoes().length > 0}>
        <div class="absolute bottom-full left-4 mb-1 w-72 overflow-hidden rounded-md border border-edge bg-panel shadow-lg z-20">
          <For each={sugestoes()}>
            {(u, i) => (
              <button
                type="button"
                class="flex w-full items-center justify-between px-3 py-1.5 text-left text-sm hover:bg-hover"
                classList={{ 'bg-hover': i() === realce() }}
                onMouseDown={(e) => (e.preventDefault(), escolher(u))}
              >
                <span class="text-ink">{u.nome}</span>
                <span class="text-xs text-ink-subtle">@{u.login}</span>
              </button>
            )}
          </For>
        </div>
      </Show>
      <div class="rounded-xl border bg-input" classList={{ 'border-accent': sobre(), 'border-edge': !sobre() }}>
        <Show when={pendentes().length > 0}>
          <div class="flex flex-wrap gap-2 px-3 pt-2">
            <For each={pendentes()}>
              {(p) => (
                <span class="inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs" classList={{ 'border-failure/50 text-failure': !!p.erro, 'border-edge text-ink-muted': !p.erro }}>
                  📎 <span class="max-w-40 truncate">{p.arquivo.name}</span> <span class="text-ink-placeholder">{tamanho(p.arquivo.size)}</span>
                  <button type="button" class="hover:text-failure" aria-label={`Tirar ${p.arquivo.name}`} onClick={() => setPendentes(pendentes().filter((x) => x.id !== p.id))}>×</button>
                </span>
              )}
            </For>
          </div>
        </Show>
        <textarea
          ref={campo}
          rows={1}
          class="block w-full resize-none bg-transparent px-3 py-2.5 text-sm text-ink outline-none placeholder:text-ink-placeholder disabled:opacity-60"
          placeholder={props.desabilitado ?? props.placeholder}
          disabled={!!props.desabilitado}
          value={texto()}
          onInput={(e) => (setTexto(e.currentTarget.value), ajustarAltura(), detectarMencao())}
          onClick={detectarMencao}
          onPaste={(e) => {
            const arquivos = [...(e.clipboardData?.files ?? [])]
            if (arquivos.length) (e.preventDefault(), adicionar(arquivos))
          }}
          onKeyDown={(e) => {
            const s = sugestoes()
            if (s.length) {
              if (e.key === 'ArrowDown') return (e.preventDefault(), setRealce((realce() + 1) % s.length))
              if (e.key === 'ArrowUp') return (e.preventDefault(), setRealce((realce() - 1 + s.length) % s.length))
              if (e.key === 'Enter' || e.key === 'Tab') return (e.preventDefault(), escolher(s[realce()]))
              if (e.key === 'Escape') return setMencao(null)
            }
            if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) (e.preventDefault(), void enviar())
          }}
        />
        <div class="flex items-center gap-2 px-2 pb-2">
          <button
            type="button"
            class="rounded px-2 py-1 text-ink-muted hover:bg-hover hover:text-ink disabled:opacity-40"
            disabled={!!props.desabilitado || !online()}
            title={online() ? 'Anexar arquivo (ou cole / arraste aqui)' : 'Anexos precisam de internet'}
            onClick={() => seletor?.click()}
          >
            📎
          </button>
          <input ref={seletor} type="file" multiple class="hidden" onChange={(e) => (adicionar(e.currentTarget.files), (e.currentTarget.value = ''))} />
          <span class="text-[11px] text-ink-placeholder hidden sm:inline">Enter envia · Shift+Enter quebra linha · @ menciona</span>
          <Button variant="accent" size="sm" class="ml-auto" disabled={!!props.desabilitado || enviando() || (!texto().trim() && !pendentes().length)} onClick={() => void enviar()}>
            {enviando() ? 'Enviando…' : 'Enviar'}
          </Button>
        </div>
      </div>
      <Show when={erro()}>
        <p class="mt-1 text-xs text-failure" role="alert">{erro()}</p>
      </Show>
    </div>
  )
}
